import React, { useMemo } from "react"
import { Box, BoxProps, Center, Flex, Text } from "@chakra-ui/react"
import Icon from "@app/components/Icon"

const PROGRESS_CARD_HEIGHT = "48px"

type Props = {
  iconName: string
  text: string
  progress: number
  messageBgColour?: string
} & BoxProps

export const CartProgressCard: React.FC<Props> = React.memo(({ iconName, text, messageBgColour, progress, ...props }) => {
  const thresholdMet = useMemo(() => progress >= 100, [progress])

  if (messageBgColour && thresholdMet) {
    return (
      <Box maxH={PROGRESS_CARD_HEIGHT} {...props}>
        <Box
          py={2}
          px={4}
          pos="relative"
          maxH={PROGRESS_CARD_HEIGHT}
          _before={{
            content: "''",
            bgColor: messageBgColour,
            borderRadius: "8px",
            pos: "absolute",
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            opacity: 1,
            zIndex: 0,
          }}
        >
          <Flex pos="relative" zIndex={1} alignItems="center" justifyContent="center" gap={2}>
            <Icon name={iconName} width="14px" height="14px" fill="none" stroke="text.primary" />
            <Text size="sm" color="text.primary">
              {text}
            </Text>
          </Flex>
        </Box>
      </Box>
    )
  }

  return (
    <Box maxH={PROGRESS_CARD_HEIGHT} {...props}>
      <Flex
        px={4}
        pos="relative"
        direction="column"
        justify="center"
        maxH={PROGRESS_CARD_HEIGHT}
        _before={{
          content: "''",
          bgColor: messageBgColour ? messageBgColour : thresholdMet ? "preset.orange" : "primary.softGrey",
          borderRadius: "8px",
          pos: "absolute",
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          opacity: messageBgColour ? 1 : thresholdMet ? 0.15 : 1,
          transition: "0.2s background-color",
          zIndex: 0,
        }}
      >
        <Flex pos="relative" zIndex={1} direction="column" pt={2} pb="1.5">
          <Flex alignItems="center" gap={2}>
            <Box flex={1} borderRadius={2} h="2px" pos="relative" overflow="hidden">
              <Box bgColor="secondary.grey" w="100%" h="100%" pos="absolute" />
              <Box
                bgColor={thresholdMet && !messageBgColour ? "preset.orange" : "primary.midGrey"}
                borderRightRadius={thresholdMet ? 0 : 2}
                w={`${progress}%`}
                h="100%"
                pos="absolute"
                transition="0.2s width, 0.2s background-color"
              />
            </Box>
            <Center
              w="14px"
              h="14px"
              minW="14px"
              borderRadius="20px"
              bg={thresholdMet && !messageBgColour ? "transparent" : messageBgColour || "primary.softGrey"}
            >
              <Icon name={iconName} width="14px" height="14px" fill="none" stroke="text.primary" />
            </Center>
          </Flex>
          <Text size="sm" textAlign="center" color="text.primary">
            {text}
          </Text>
        </Flex>
      </Flex>
    </Box>
  )
})
